import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { green, red, grey } from '@mui/material/colors';

const accesstoken = () => sessionStorage.getItem('accesstoken');

// date input <-> epoch helpers (local time)
const toStart = (d) => new Date(`${d}T00:00:00`).getTime();
const toEnd = (d) => new Date(`${d}T23:59:59`).getTime();

function defaultRanges() {
    const today = dayjs();
    const p1End = today;
    const p1Start = today.subtract(13, 'day');
    const p2End = p1Start.subtract(1, 'day');
    const p2Start = p2End.subtract(13, 'day');
    return {
        p1Start: p1Start.format('YYYY-MM-DD'),
        p1End: p1End.format('YYYY-MM-DD'),
        p2Start: p2Start.format('YYYY-MM-DD'),
        p2End: p2End.format('YYYY-MM-DD'),
    };
}

function UsageReport() {
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [ranges, setRanges] = useState(defaultRanges());
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`/api/admin/organizations?accesstoken=${accesstoken()}`);
                if (Array.isArray(data)) {
                    setOrgs(data);
                } else {
                    setOrgs([]);
                    setError('Could not load workspaces — is the backend server running the latest build? Restart it (npm run start:dev:server).');
                }
            } catch (e) {
                setOrgs([]);
                setError('Failed to load workspaces: ' + (e.response?.status || e.message));
            }
        })();
    }, []);

    const run = async () => {
        if (!orgId) { setError('Pick a workspace first'); return; }
        setLoading(true); setError(null); setReport(null);
        try {
            const params = new URLSearchParams({
                accesstoken: accesstoken(),
                organizationId: orgId,
                p1Start: toStart(ranges.p1Start), p1End: toEnd(ranges.p1End),
                p2Start: toStart(ranges.p2Start), p2End: toEnd(ranges.p2End),
            });
            const { data } = await axios.get(`/api/admin/usage-report?${params.toString()}`);
            setReport(data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Report failed');
        } finally {
            setLoading(false);
        }
    };

    const money = (n) => {
        const cur = report?.org?.currency || 'USD';
        try {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n || 0);
        } catch (e) {
            return `${Math.round(n || 0).toLocaleString()} ${cur}`;
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 800 }}>Workspace Usage Report</Typography>

            {/* Controls */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField
                        select label='Workspace' value={orgId} onChange={e => setOrgId(e.target.value)}
                        size='small' sx={{ minWidth: 240 }}
                    >
                        {orgs.map(o => <MenuItem key={o.id} value={o.id}>{o.alias}{o.title && o.title !== o.alias ? ` — ${o.title}` : ''}</MenuItem>)}
                    </TextField>
                    <DateBox label='Period 1 start' value={ranges.p1Start} onChange={v => setRanges(r => ({ ...r, p1Start: v }))} />
                    <DateBox label='Period 1 end' value={ranges.p1End} onChange={v => setRanges(r => ({ ...r, p1End: v }))} />
                    <DateBox label='Period 2 start' value={ranges.p2Start} onChange={v => setRanges(r => ({ ...r, p2Start: v }))} />
                    <DateBox label='Period 2 end' value={ranges.p2End} onChange={v => setRanges(r => ({ ...r, p2End: v }))} />
                    <Button variant='contained' onClick={run} disabled={loading} sx={{ height: 40 }}>
                        {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Run report'}
                    </Button>
                </Box>
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                    Period 1 = current window · Period 2 = comparison window. Figures are computed live from production data.
                </Typography>
            </Paper>

            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

            {report && !loading && <Report report={report} money={money} />}
        </Box>
    );
}

function DateBox({ label, value, onChange }) {
    return (
        <TextField
            type='date' label={label} value={value} onChange={e => onChange(e.target.value)}
            size='small' InputLabelProps={{ shrink: true }} sx={{ width: 165 }}
        />
    );
}

// ─── Report body ───
function Report({ report, money }) {
    const r = report;
    const dq = r.dataQuality;
    const p1 = r.totals.p1, p2 = r.totals.p2;
    const delta = (a, b) => {
        const d = a - b;
        const sign = d > 0 ? '+' : '';
        return <span style={{ color: d > 0 ? green[700] : d < 0 ? red[700] : grey[600] }}>{sign}{d.toLocaleString()}</span>;
    };

    return (
        <Box>
            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                {r.org.alias}{r.org.title && r.org.title !== r.org.alias ? ` — ${r.org.title}` : ''}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
                P1 {dayjs(r.periods.p1.start).format('DD MMM')}–{dayjs(r.periods.p1.end).format('DD MMM YYYY')} ·
                P2 {dayjs(r.periods.p2.start).format('DD MMM')}–{dayjs(r.periods.p2.end).format('DD MMM YYYY')} ·
                {' '}{r.counts.opportunities} opps · {r.counts.accounts} accounts · {r.counts.users} users
            </Typography>

            <Alert severity='warning' sx={{ my: 2 }}>
                <b>Profit is partial.</b> {dq.wonZeroCost} of {dq.wonDeals} won deals have no supplier cost entered (margins show ~100%).
                Revenue is reliable; profit is a floor. {dq.outliersExcluded} deal(s) with corrupted cost data are excluded from profit rankings.
            </Alert>

            {/* Totals */}
            <Section title='Totals'>
                <Table size='small'>
                    <TableHead><Row cells={['Metric', 'Period 1', 'Period 2', 'Δ']} head /></TableHead>
                    <TableBody>
                        <Row cells={['Quotes created', p1.created, p2.created, delta(p1.created, p2.created)]} />
                        <Row cells={['Quotes sent', p1.sent, p2.sent, delta(p1.sent, p2.sent)]} />
                        <Row cells={['Quotes won', p1.won, p2.won, delta(p1.won, p2.won)]} />
                        <Row cells={['Won revenue', money(p1.revenue), money(p2.revenue), '']} />
                        <Row cells={['Won profit (captured)', money(p1.profit), money(p2.profit), '']} />
                        <Row cells={['Active creators', p1.activeCreators, p2.activeCreators, delta(p1.activeCreators, p2.activeCreators)]} />
                    </TableBody>
                </Table>
            </Section>

            {/* Per-user */}
            <Section title='Per-user activity'>
                <Table size='small'>
                    <TableHead><Row head cells={['User', 'Office', 'Cr P1', 'Cr P2', 'Sent P1', 'Sent P2', 'Won P1', 'Won P2', 'Rev P1', 'Rev P2']} /></TableHead>
                    <TableBody>
                        {r.perUser.map(u => <Row key={u.email} cells={[u.user, u.office, u.crP1, u.crP2, u.snP1, u.snP2, u.wnP1, u.wnP2, money(u.revP1), money(u.revP2)]} />)}
                    </TableBody>
                </Table>
            </Section>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Section title='Champions — by quotes sent (P1)' half>
                    <Table size='small'>
                        <TableHead><Row head cells={['User', 'Office', 'Sent', 'Won', 'Revenue']} /></TableHead>
                        <TableBody>{r.champions.bySent.map(u => <Row key={u.email} cells={[u.user, u.office, u.snP1, u.wnP1, money(u.revP1)]} />)}</TableBody>
                    </Table>
                </Section>
                <Section title='Champions — by won revenue (P1)' half>
                    <Table size='small'>
                        <TableHead><Row head cells={['User', 'Office', 'Revenue', 'Won', 'Sent']} /></TableHead>
                        <TableBody>{r.champions.byRevenue.map(u => <Row key={u.email} cells={[u.user, u.office, money(u.revP1), u.wnP1, u.snP1]} />)}</TableBody>
                    </Table>
                </Section>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Section title='New users (active P1, not P2)' half>
                    {r.newUsers.length === 0 ? <Empty /> : (
                        <Table size='small'>
                            <TableHead><Row head cells={['User', 'Office', 'Created P1', 'Sent P1']} /></TableHead>
                            <TableBody>{r.newUsers.map(u => <Row key={u.email} cells={[u.user, u.office, u.crP1, u.snP1]} />)}</TableBody>
                        </Table>
                    )}
                </Section>
                <Section title='Using the system less (P1 < P2)' half>
                    {r.declining.length === 0 ? <Empty /> : (
                        <Table size='small'>
                            <TableHead><Row head cells={['User', 'Office', 'P1 activity', 'P2 activity', 'Δ']} /></TableHead>
                            <TableBody>{r.declining.map(u => <Row key={u.email} cells={[u.user, u.office, u.act1, u.act2, u.act1 - u.act2]} />)}</TableBody>
                        </Table>
                    )}
                </Section>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Section title='Draft-only / high-draft users' half>
                    {r.draftOnly.length === 0 ? <Empty /> : (
                        <Table size='small'>
                            <TableHead><Row head cells={['User', 'Office', 'Created P1', 'Still draft', 'Sent P1']} /></TableHead>
                            <TableBody>{r.draftOnly.map(u => <Row key={u.email} cells={[<>{u.user}{u.allDraft && <Chip size='small' label='all draft' sx={{ ml: 1, height: 18 }} color='warning' />}</>, u.office, u.crP1, u.draftP1, u.snP1]} />)}</TableBody>
                        </Table>
                    )}
                </Section>
                <Section title='Account-info activity (updates by user)' half>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                        Targets/tradelines created — P1: {r.targets.createdP1} · P2: {r.targets.createdP2} · total: {r.targets.total}
                    </Typography>
                    <Table size='small'>
                        <TableHead><Row head cells={['User', 'Office', 'Updates P1', 'Updates P2']} /></TableHead>
                        <TableBody>{r.accountUpdates.map(u => <Row key={u.email} cells={[u.user, u.office, u.p1, u.p2]} />)}</TableBody>
                    </Table>
                </Section>
            </Box>

            {/* Tradeline profitability */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <LaneTable title='Top lanes by revenue (P1)' lanes={r.tradelines.byRevenueP1} money={money} mode='revenue' />
                <LaneTable title='Top lanes by profit (P1)' lanes={r.tradelines.byProfitP1} money={money} mode='profit' />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <LaneTable title='Top lanes by revenue (all-time)' lanes={r.tradelines.byRevenueAllTime} money={money} mode='revenue' />
                <LaneTable title='Top lanes by profit (all-time)' lanes={r.tradelines.byProfitAllTime} money={money} mode='profit' />
            </Box>

            {/* Losing */}
            <Section title='Losing lanes — sent but never won (≥3 sent)'>
                <Table size='small'>
                    <TableHead><Row head cells={['Lane', 'Sent', 'Lost', 'Stale (≥14d pending)']} /></TableHead>
                    <TableBody>{r.losing.lanesNoWin.map(l => <Row key={l.lane} cells={[l.lane, l.sent, l.lost, l.stale]} />)}</TableBody>
                </Table>
            </Section>
            <Section title='Losing lanes — worst win-rate (≥6 sent, <25%)'>
                <Table size='small'>
                    <TableHead><Row head cells={['Lane', 'Sent', 'Won', 'Win %', 'Lost', 'Stale']} /></TableHead>
                    <TableBody>{r.losing.lanesWorstRate.map(l => <Row key={l.lane} cells={[l.lane, l.sent, l.won, `${Math.round(l.winRate)}%`, l.lost, l.stale]} />)}</TableBody>
                </Table>
            </Section>
            <Section title='Accounts — most sent, zero won (≥3 sent)'>
                <Table size='small'>
                    <TableHead><Row head cells={['Account', 'Sent', 'Lost']} /></TableHead>
                    <TableBody>{r.losing.accounts.map(a => <Row key={a.accountId} cells={[a.name, a.sent, a.lost]} />)}</TableBody>
                </Table>
            </Section>
        </Box>
    );
}

function LaneTable({ title, lanes, money, mode }) {
    return (
        <Section title={title} half>
            <Table size='small'>
                <TableHead><Row head cells={['Lane', 'Wins', mode === 'revenue' ? 'Revenue' : 'Profit', 'Margin']} /></TableHead>
                <TableBody>
                    {lanes.map(l => <Row key={l.lane} cells={[l.lane, l.wins, money(mode === 'revenue' ? l.revenue : l.profitClean), `${Math.round(l.margin)}%`]} />)}
                </TableBody>
            </Table>
        </Section>
    );
}

function Section({ title, children, half }) {
    return (
        <Box sx={{ mb: 3, flex: half ? '1 1 480px' : '1 1 100%', minWidth: half ? 360 : 'auto' }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
            <TableContainer component={Paper}>{children}</TableContainer>
        </Box>
    );
}

function Row({ cells, head }) {
    return (
        <TableRow>
            {cells.map((c, i) => (
                <TableCell key={i} sx={{ fontWeight: head ? 700 : 400, whiteSpace: 'nowrap' }} align={i === 0 ? 'left' : 'right'}>{c}</TableCell>
            ))}
        </TableRow>
    );
}

function Empty() {
    return <Box sx={{ p: 2 }}><Typography variant='body2' color='text.secondary'>None</Typography></Box>;
}

export default UsageReport;
